import React from 'react';
import { useTranslation } from 'next-i18next';

import { PageSeo } from 'components/common';
import { BlogLayout } from 'components/layout';
import { APP_ROUTES } from 'constants/routes';
import { TRANSLATIONS } from 'constants/translations';
import { getRecipesByTagPageSeo } from 'helpers/SeoHelpers/RecipesSeoHelpers';
import { createStaticPaths, prefetchConfig } from 'helpers/ServerHelpers';
import { withSSGPage } from 'hoc/WithServerPage/withSSGPage';
import { prefetchRecipesByTag, useRecipesByTag } from 'hooks/data/useRecipesByTag';

import { RecipesByTagTemplate, RecipesByTagTemplateStyles as styles } from 'templates/Recipes';

import { type NextPageWithLayout, PageType } from 'types';

export const getStaticPaths = createStaticPaths();

export const getStaticProps = withSSGPage(async (context, props) => {

  const {
    queryClient, preview, urlParams
  } = props;
  const { identifier: tag } = urlParams;

  if (!tag) {
    return {
      redirect: {
        permanent: false,
        destination: APP_ROUTES.RECIPES_LANDING
      }
    };
  }

  const [isDataFound] = await Promise.all([
    prefetchRecipesByTag({ ...urlParams, preview }, queryClient),
    prefetchConfig(queryClient)
  ]);

  if (!isDataFound) {
    return {
      notFound: true,
      revalidate: 60
    };
  }

  return {
    props: {}
  };

}, {
  translations: [TRANSLATIONS.COMMON, TRANSLATIONS.RECIPES, TRANSLATIONS.BLOG],
  pageType: PageType.RecipesByTag,
  revalidate: 10
});

// --- Page

const RecipesByTag: NextPageWithLayout<PageType.RecipesByTag> = (props) => {
  const { preview, urlParams } = props;

  const { data } = useRecipesByTag({ ...urlParams, preview });
  const { t } = useTranslation();

  const pageSeoProps = getRecipesByTagPageSeo(t, data);

  return (
    <>
      <PageSeo {...pageSeoProps} />
      <RecipesByTagTemplate
        preview={preview}
        urlParams={urlParams}
      />
    </>
  );
};

// --- Layout

const layoutProps = {
  containerClassName: styles.layoutContainer,
  contentClassName: styles.layoutContent,
  header: {
    props: {},
    isVisible: true,
  },
  footer: {
    props: {},
    isVisible: true
  },
  openInAppBanner: {
    props: {},
    isVisible: true
  }
};

RecipesByTag.getLayout = (page, { preview }) => (
  <BlogLayout
    {...layoutProps}
    preview={preview}
  >
    {page}
  </BlogLayout>
);

// --- Export

export default RecipesByTag;
